import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Animated } from 'react-animated-css';
import PercentageProgressRing from '../../components/PercentageProgressRing';
import { Grid } from '@material-ui/core';
import useTeethSelections from '../hooks/DiffRetainerHooks/useTeethSelections';
import useRetainerRequestForm, { IMPRESSION_TYPE } from '../hooks/DiffRetainerHooks/useRetainerRequestForm';
import { getUpperAndLowerImpressionTypeSchema } from '../../components/DifferentRetainer/YupValidation';
import {
    RESET_FORM_VALUES,
    RESET_TOOTH_SELECTIONS,
    SET_TARGET_TYPE_FORM_VALIDATION_FUNC,
} from '../../store/DiffRetainers/actionType';
import useRetainerRequestSetFormValue from '../hooks/DiffRetainerHooks/useRetainerRequestSetFormValue';
import useSetSelectedToothFromResponse from '../hooks/DiffRetainerHooks/useSetSelectedToothFromResponse';
import { DiffRetainerLangContext } from '../../components/DifferentRetainer/PrescriptionNote/hooks/useDiffRetainerLangContext';
import { useLang } from '../../util/lang';
import DiffRetainerForm from './DiffRetainerForm';

const DiffRetainerTab = (props) => {
    const { handleSaveDraft, caseId, tabMode } = props;
    const getDiffRetainerLang = useLang('DiffRetainer');
    const dispatch = useDispatch();
    const getGlobalLang = useLang('global');

    const [loading, setLoading] = useState(false);
    const [impressionType, setImpressionType] = useState(undefined);

    const {
        baseTeethSelections,
        upperAndLowerTeethSelections,
        handleFormValuesWithTeethSelections,
        resetAllTeethSelections,
    } = useTeethSelections();

    const singleRetainerTypeFormProps = useRetainerRequestForm({ schema: null, isTab: true });
    const upperAndLowerRetainerSchema = getUpperAndLowerImpressionTypeSchema(
        getGlobalLang('@global.const.required'),
        true
    );
    const upperAndLowerRetainerTypeFormProps = useRetainerRequestForm({
        schema: upperAndLowerRetainerSchema,
        isTab: true,
    });
    const [specialInstruction, setSpecialInstruction] = useState('');

    // handle set form value from store (API response) to this component
    useRetainerRequestSetFormValue(
        singleRetainerTypeFormProps,
        upperAndLowerRetainerTypeFormProps,
        props.NewPatientData,
        setImpressionType,
        setSpecialInstruction
    );

    // handle set selected teeth from store (API response) to this component
    useSetSelectedToothFromResponse({ impressionType, baseTeethSelections, upperAndLowerTeethSelections });

    // need to reset form values and teeth selections from store when component unmount
    useEffect(() => {
        return () => {
            dispatch({ type: 'NEW_PATIENT_RESET_RETAINER_REQUEST' });
            dispatch({ type: RESET_TOOTH_SELECTIONS });
            dispatch({ type: RESET_FORM_VALUES });
        };
    }, []);

    // set validation function for each impression type into store
    useEffect(() => {
        if (!impressionType) return;
        const singleTypeValidationFunc = singleRetainerTypeFormProps.methods.trigger;
        const upperLowerValidationFunc = upperAndLowerRetainerTypeFormProps.methods.trigger;
        const payload =
            impressionType === IMPRESSION_TYPE.UPPER_AND_LOWER ? upperLowerValidationFunc : singleTypeValidationFunc;
        dispatch({ type: SET_TARGET_TYPE_FORM_VALIDATION_FUNC, payload: payload });
    }, [
        impressionType,
        singleRetainerTypeFormProps.methods.trigger,
        upperAndLowerRetainerTypeFormProps.methods.trigger,
    ]);

    const progressBarRef = useRef(null);

    const baseProps = {
        singleRetainerTypeFormProps,
        upperAndLowerRetainerTypeFormProps,
        baseTeethSelections,
        upperAndLowerTeethSelections,
        handleFormValuesWithTeethSelections,
        resetAllTeethSelections,

        impressionType,
        setImpressionType,
        specialInstruction,
        setSpecialInstruction,
    };

    return (
        <>
            <Animated animationIn="fadeIn" isVisible={true} style={{ backgroundColor: 'rgb(242, 242, 242)' }}>
                <PercentageProgressRing ref={progressBarRef} open={loading} />
                <DiffRetainerLangContext.Provider value={{ getDiffRetainerLang }}>
                    <Grid container style={{ padding: 16, background: '#fff' }}>
                        <Grid item xs={12}>
                            <DiffRetainerForm
                                setLoading={setLoading}
                                onProgressChange={progressBarRef?.current?.onProgressChange}
                                isNewCase={true}
                                baseProps={baseProps}
                                caseId={caseId}
                                isTab={true}
                                tabMode={tabMode}
                            />
                        </Grid>
                    </Grid>
                </DiffRetainerLangContext.Provider>
            </Animated>
        </>
    );
};

const mapStateToProps = (store) => {
    return {
        NewPatientData: store.newPatientReducer,
        files: store.newPatientReducer.files,
        config: store.config.config,
        lang: store.lang,
        isLoading: store.loading.isLoading,
        diffRetainer: store.diffRetainer,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DiffRetainerTab));
